import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../../core/sdk';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatDate } from '../../../../utils/helper';
import { RequestStatus } from '../../ShareComponents';
import { Tooltip } from 'primereact/tooltip';

const RequestDialog = observer(({ sdk, store }: { sdk: ApplicationSdk; store?: any }) => {
    const dialogStore = store || sdk.currentDialog;

    const handleAcceptRequest = async () => {
        const data = await sdk.getDialogMultiInput({
            title: 'Antrag annehmen',
            message: 'Möchtest du diesen Antrag annehmen?',
            fields: [
                {
                    key: 'comment',
                    label: 'Kommentar (optional)',
                    placeholder: 'Optionaler Kommentar zur Annahme des Antrags...',
                    type: 'textarea',
                    required: false,
                    rows: 4,
                },
            ],
        });

        if (data.confirmed && data.values) {
            try {
                const response = await dialogStore.onApproveRequest(data.values.comment);
                if (response.success) {
                    sdk.showSuccessToast('Antrag erfolgreich angenommen');
                    sdk.closeDialog({ event: 'ACCEPTED' });
                } else {
                    sdk.showErrorToast('Fehler beim Annehmen des Antrags');
                }
            } catch (error) {
                // sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
            }
        }
    };

    const handleRejectRequest = async () => {
        const data = await sdk.getDialogMultiInput({
            title: 'Antrag ablehnen',
            message: 'Möchtest du diesen Antrag ablehnen?',
            fields: [
                {
                    key: 'comment',
                    label: 'Ablehnungsgrund',
                    placeholder: 'Ein Ablehnungsgrund ist erforderlich',
                    type: 'textarea',
                    rows: 4,
                    required: true,
                },
            ],
        });

        if (data.confirmed && data.values) {
            try {
                const response = await dialogStore.onRejectRequest(data.values.comment);
                if (response.success) {
                    sdk.showSuccessToast('Antrag abgelehnt');
                    sdk.closeDialog({ event: 'REJECTED' });
                } else {
                    sdk.showErrorToast('Fehler beim Ablehnen des Antrags');
                }
            } catch (error) {
                // sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
            }
        }
    };

    const handleSave = async () => {
        try {
            await dialogStore.onSaveRequest();
            // Erfolgreich => Schließe nun die Ansicht
            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Antrag erfolgreich gespeichert');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };

    const requestTypeOptions = dialogStore.availableRequestTypes.map((rt: any) => ({
        label: rt.name,
        value: rt.id,
    }));

    const dialogHeader = `Antrag bearbeiten - ID: ${dialogStore.request_id || 'Neu'}`;
    const isNewDialog: boolean = dialogStore?.isNewRequest || false;

    return (
        <Dialog
            visible={!!sdk.currentDialog}
            modal
            header={dialogHeader}
            footer={() => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {/* Action buttons for pending requests */}
                    {dialogStore.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                                label="Annehmen"
                                icon="pi pi-check"
                                className="p-button-success"
                                onClick={handleAcceptRequest}
                                disabled={dialogStore.saving}
                            />
                            <Button
                                label="Ablehnen"
                                icon="pi pi-times"
                                className="p-button-danger"
                                onClick={handleRejectRequest}
                                disabled={dialogStore.saving}
                            />
                        </div>
                    )}

                    {/* Standard dialog buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={() => sdk.closeDialog()}
                            disabled={dialogStore.saving}
                        />
                        {dialogStore.isNewRequest ? (
                            <Button
                                label="Speichern"
                                icon="pi pi-check"
                                className="p-button-primary"
                                onClick={handleSave}
                                disabled={!dialogStore.canSave || dialogStore.saving}
                                loading={dialogStore.saving}
                            />
                        ) : null}
                    </div>
                </div>
            )}
            onHide={() => sdk.closeDialog()}
            style={{ width: '50rem' }}
            maximizable
        >
            {dialogStore.requestDataLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <ProgressSpinner />
                    <p>Antrag wird geladen...</p>
                </div>
            ) : (
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="status">Status</label>
                        <div style={{ marginTop: '0.5rem' }}>
                            <RequestStatus status={dialogStore.status} />
                        </div>
                    </div>
                    {dialogStore.isNewRequest ? null : (
                        <div className="formgrid grid">
                            <div className="field col-6">
                                <label htmlFor="created">Erstellt am</label>
                                <InputText
                                    id="created"
                                    value={
                                        dialogStore.created ? formatDate(dialogStore.created) : ''
                                    }
                                    disabled
                                    style={{ marginTop: '0.5rem' }}
                                />
                            </div>
                            <div className="field col-6">
                                <label htmlFor="updated">Aktualisiert am</label>
                                <InputText
                                    id="updated"
                                    value={
                                        dialogStore.updated ? formatDate(dialogStore.updated) : ''
                                    }
                                    disabled
                                    style={{ marginTop: '0.5rem' }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="field">
                        <label htmlFor="user">Antragsteller</label>
                        {isNewDialog ? (
                            <Dropdown
                                id="employeeDropdown"
                                value={dialogStore.selectedEmployees || 0}
                                options={[
                                    { label: 'Keine Reinigungskraft', value: 0 }, // Add this option for null
                                    ...(sdk.employee?.all
                                        .filter(dialogStore.currentEmployeeFilter)
                                        .map((employee: any) => ({
                                            label: employee.name,
                                            value: employee.id,
                                        })) || []),
                                ]}
                                onChange={e => dialogStore.selectEmployee(e.value)}
                                placeholder="Reinigungskraft auswählen"
                                filter
                                onFilter={e => {
                                    dialogStore.filterEmployees(e.filter);
                                }}
                                loading={sdk.employee?.loading || false}
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <InputText
                                id="user"
                                value={dialogStore.employee_name || ''}
                                disabled
                                style={{ marginTop: '0.5rem' }}
                            />
                        )}
                    </div>
                    <div className="field">
                        <label htmlFor="requestType">Antragstyp *</label>
                        <Dropdown
                            id="requestType"
                            value={dialogStore.request_type_id}
                            options={requestTypeOptions}
                            onChange={e => dialogStore.setRequestType(e.value)}
                            placeholder="Antragstyp auswählen"
                            style={{ marginTop: '0.5rem' }}
                            disabled={!isNewDialog}
                        />
                    </div>
                    <div className="formgrid grid">
                        <div className="field col-6">
                            <label
                                htmlFor="startDate"
                                style={{ display: 'block', marginBottom: '0.5rem' }}
                            >
                                Startdatum *
                            </label>
                            <Calendar
                                id={'startDate'}
                                value={dialogStore.start_date}
                                onChange={e => {
                                    dialogStore.startDate = e.target.value || null;
                                }}
                                showIcon
                                locale="de"
                                style={{ width: '100%' }}
                                dateFormat={'dd.mm.yy'}
                                data-pr-tooltip="Bitte gebe ein Startdatum an"
                                data-pr-position="top"
                                data-pr-disabled={dialogStore.isStartDateValid}
                                invalid={!dialogStore.isStartDateValid}
                                data-testid={'request-startdate-datepicker'}
                                disabled={!isNewDialog}
                            />
                            <Tooltip target="#startDate" />
                        </div>
                        <div className="field col-6">
                            <label
                                htmlFor="endDate"
                                style={{ display: 'block', marginBottom: '0.5rem' }}
                            >
                                Enddatum *
                            </label>
                            <Calendar
                                id={'endDate'}
                                value={dialogStore.end_date}
                                onChange={e => {
                                    dialogStore.endDate = e.target.value || null;
                                }}
                                showIcon
                                locale="de"
                                style={{ width: '100%' }}
                                dateFormat={'dd.mm.yy'}
                                data-pr-tooltip="Bitte gebe ein Enddatum an"
                                data-pr-position="top"
                                data-pr-disabled={dialogStore.isEndDateValid}
                                invalid={!dialogStore.isEndDateValid}
                                data-testid={'request-enddate-datepicker'}
                                disabled={!isNewDialog}
                            />
                            <Tooltip target="#endDate" />
                        </div>
                    </div>
                    {dialogStore.start_date &&
                        dialogStore.end_date &&
                        !dialogStore.isValidDateRange && (
                            <div className="field">
                                <small className="p-error">
                                    Das Startdatum darf nicht nach dem Enddatum liegen.
                                </small>
                            </div>
                        )}
                    <div className="field">
                        <label htmlFor="comment">Kommentar</label>
                        <InputTextarea
                            id="comment"
                            value={dialogStore.comment}
                            onChange={e => dialogStore.setComment(e.target.value)}
                            placeholder="Optionaler Kommentar zum Antrag..."
                            rows={3}
                            style={{ marginTop: '0.5rem' }}
                            disabled={!isNewDialog}
                        />
                    </div>
                    {(dialogStore.status === 'approved' || dialogStore.status === 'rejected') && (
                        <>
                            <div className="field">
                                <label htmlFor="approverName">
                                    {dialogStore.status === 'approved'
                                        ? 'Genehmigt von'
                                        : 'Abgelehnt von'}
                                </label>
                                <InputText
                                    id="approverName"
                                    value={dialogStore.approver_name}
                                    disabled
                                    style={{ marginTop: '0.5rem' }}
                                />
                            </div>
                            {dialogStore.approved_at && (
                                <div className="field">
                                    <label htmlFor="approvedAt">
                                        {dialogStore.status === 'approved'
                                            ? 'Genehmigt am'
                                            : 'Abgelehnt am'}
                                    </label>
                                    <InputText
                                        id="approvedAt"
                                        value={formatDate(dialogStore.approved_at)}
                                        disabled
                                        style={{ marginTop: '0.5rem' }}
                                    />
                                </div>
                            )}

                            {dialogStore.approver_comment && (
                                <div className="field">
                                    <label htmlFor="approverComment">
                                        {dialogStore.status === 'approved'
                                            ? 'Genehmigungskommentar'
                                            : 'Ablehnungsgrund'}
                                    </label>
                                    <InputTextarea
                                        id="approverComment"
                                        value={dialogStore.approver_comment}
                                        disabled
                                        rows={3}
                                        style={{ marginTop: '0.5rem' }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {/* {dialogStore.status !== 'pending' && (
                        <div className="field">
                            <small className="p-error">
                                Hinweis: Nur neue Anträge können bearbeitet werden.
                            </small>
                        </div>
                    )} */}
                </div>
            )}
        </Dialog>
    );
});

export default RequestDialog;
